// PartyStatement — HOW THIS PARTY'S BOX WAS ARRIVED AT.
//
// Operator, 4 Sept 2026: "…+ peerticular party trades/movement histroy — that
// should be there for verification."
//
// The party page already says what stands between the desk and this party.
// It has never said WHY. This section is the working: every entry that
// touched them — trades and money alike — NEWEST FIRST, each carrying the
// balance that stood after it, so the top row IS the Balances section above
// it. That tie is the whole point; when it cannot be claimed (a page still
// unread, a window that stops in the past), the section says so rather than
// letting the top figure on screen pass for the answer.
//
// ONE ROW PER ENTRY, A COLUMN PER CURRENCY — THE JOURNAL (operator, 21 Sept
// 2026, on Home: "trades cause money movements as well … entries multiple
// times and repeating patterns"; then "go ahead, start with the party page").
// The route serves one row per LEG — one (transaction, asset) pair with the
// signed movement and the balance in that asset after it. From 17 Sept the
// legs stood in one ledger per currency, stacked, so a trade appeared once
// in its AED ledger and once in its USDT ledger, and what one trade did to
// the party's box had to be read across two blocks. The journal folds the
// legs back onto their transaction:
//
//   head    — one line with a word on each currency's tie: whether the top
//             row IS that currency's box in Balances, or is the balance on
//             the window's closing day, or does not tie (in the warning
//             colour, with the two figures).
//   rows    — When · Type · Particulars · (Reverse) · Movement · Balance,
//             newest first, day rows between days — the bank statement's
//             two figure columns, the currency code muted on each figure. A
//             trade is ONE row with two lines in those seats, one per
//             currency; a receipt one line. The movement is signed the way
//             every figure on the desk is — positive is the box going up,
//             they owe the desk more; Balance is the balance in that
//             currency after the entry. (A column per currency was tried
//             first, 21 Sept: on a five-currency party most of the grid was
//             blank — operator, 22 Sept: "the prevailing totals and trade +
//             & − can be displayed in a better way".)
//   foot    — the brought-forward row of a windowed statement, balances only.
//
// With an asset chip chosen the Balance column is one currency's running
// balance, top to bottom — the per-currency statement, the same journal
// narrowed.
//
// THE SIGN LAW is said ONCE on the party page, under Balances (SignLaw) — this
// section sits directly beneath it and does not say it again (§5.18).
//
// The asset chips are built from every asset that ever MOVED, not from the
// ones still carrying a balance today; the chip list below says why that
// distinction was hiding most of this desk's history. With a chip chosen the
// route filters the rows and one ledger stands.
//
// A PERIOD can be put on it — the shared PeriodChips, the same row every log
// uses. The route carries a window and builds the brought-forward row that
// makes a windowed statement tie, so "show me this month" is one chip — and a
// window that stops in the past stops claiming to reach today's box, because
// it does not.
//
// Minimised 14 Sept 2026 (sweep §5.18 / item 58) and again 15 Sept: no
// subtitle, no second sign-law sentence, no chip-row labels (the chips say
// what they are), no reference line under the row (the row's tooltip carries
// it; the sentence opens the entry where the reference lives), no "now" mark
// (the ledger header says whether the top row is the box), balances in the
// desk's own rounding (a header that fails to tie prints its figure to the
// fils, beside the figure it disagrees with; what to do about it is in
// StatementHelp, not on the line).
//
// REVERSING AN ENTRY FROM THE PARTY PAGE (operator, 17 Sept 2026: "need
// options in party pages & money/trade screens"). Every live money entry —
// not a trade, which opens on the Trades log where Void lives — carries the
// same door Money movements has (components/ReverseEntry): a small button on
// the row, the panel beneath it. The entry leaves the live statement on the
// refresh that follows, so the success line sits above the ledgers.
//
// ON THE ONE LEDGER PRIMITIVE (17 Sept 2026, the same day, the operator on a
// wide monitor: "fonts etc are too small, movement type is too short,
// description lines are not in sync"). Every width, size and colour on the
// table and the cards comes from components/Ledger — the type pill in a
// column of its own so every sentence starts on one line, the reverse door
// in a column of its own so the figures never shift, the block capped so the
// figures sit near the words. Nothing about sizes is decided in this file.

import { Fragment, useMemo, useState, type ReactNode } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { ScrollText } from 'lucide-react';
import { api, type StatementRow, type MovementState } from '../lib/api';
import { readWords } from '../lib/errors';
import { uiV2 } from '../lib/ui-v2';
import { Pending, StateLine, EmptyState, WarnLine } from './kit';
import { Section } from './Section';
import { Chip } from './FilterRail';
import { PeriodChips, periodDays, type PeriodValue } from './PeriodChips';
import {
  ReverseEntryButton, ReverseEntryPanel, ReversedLine, canReverse,
  type ReversibleEntry, type ReversedOutcome,
} from './ReverseEntry';
import { LEDGER, KindPill, DayRow, Delta, Figure } from './Ledger';
import { displayAsset, fmtAmount, fmtTime, fmtDay, fmtWeekday, dateBucket, dubaiDate } from '../lib/format';
import { ledgerTypeLabel, sameAsset, foldAsset } from '../lib/naming';

// The desk's whole history is a few hundred rows; one read is almost always
// the lot. The cursor is what keeps the tie honest once it is not.
const PAGE = 200;

/** WHEN TWO READS OF THE SAME MONEY COUNT AS AGREEING.
 *
 *  Three figures meet in a ledger's header: the running balance the statement
 *  summed (exact `numeric` in Postgres, one row per leg), the party's box the
 *  same route returns (summed per raw asset code in Postgres, then folded
 *  across codes — USDT·TRC + USDT·ERC + USDC — in JavaScript, which is a float
 *  addition), and the balance the party page renders above (the ledger's own
 *  HTTP API, divided down in JavaScript). They are the same money and agree to
 *  the fils, but the two that pass through JavaScript can carry a binary-float
 *  tail: a stablecoin position in the tens of millions with a fractional part
 *  is off in its ninth decimal after one addition. So the floor is a millionth
 *  — the finest unit any asset on this desk is quoted in (USDT is stored at
 *  6dp), far below anything settled on, and above every float tail. Anything
 *  BIGGER than this is two reads disagreeing about real money, which is worth
 *  the operator's attention precisely because he came here to verify.
 *
 *  It was 1e-9 for the box-against-statement tie, on the belief that both
 *  sides came out of Postgres exact; the fold across codes made that false and
 *  a 16,777,216.40 USDT position read "does not tie" against itself. */
const AGREE_EPSILON = 1e-6;

/** THE PRECISION A RECONCILIATION IS READ AT.
 *
 *  AED and INR round to whole units everywhere else on the desk, on purpose —
 *  the fils are noise. Not when a ledger fails to tie: the two figures then
 *  exist to be compared, and "1,00,000 against 1,00,000" is not a check when
 *  the two are 40 fils apart. Two places is the floor; a 6dp asset whose two
 *  reads differ in the sixth place is read at six, or the difference the
 *  operator is told about is one he cannot see (see reconcileDp). */
const RECONCILE_DP = 2;
const RECONCILE_DP_MAX = 8;

/** The fewest decimals, from RECONCILE_DP up, at which two figures that do
 *  not agree print differently. */
function reconcileDp(a: number, b: number): number {
  for (let d = RECONCILE_DP; d < RECONCILE_DP_MAX; d++) if (a.toFixed(d) !== b.toFixed(d)) return d;
  return RECONCILE_DP_MAX;
}

/** THE STATEMENT'S PART OF "ABOUT THIS SCREEN" on the party page — the sentences that used to sit on the section as a
 *  subtitle, a column hint and a 60-word tooltip (§5.18). Rendered inside the page's ScreenHelp, which is a tap away, so
 *  the word budget does not apply to it — but the gate cannot see a help slot in another file, hence the markers.
 *
 *  21 Oct 2026 sweep: tightened, and the paragraph about a desk account's statement is gone — it was drawn on every
 *  customer's and merchant's page, where it is not true of anything (a desk account has no party page; Treasury opens it). */
export function StatementHelp() {
  return (
    <>
      {/* words:ok — About text, drawn only inside the party page's help= */}
      <p>
        One row per entry, newest first: the movement (positive = the box goes up, they owe more) and the
        balance in that currency after it. A trade is one row with a line for each of its two currencies.
      </p>
      {/* words:ok — About text, drawn only inside the party page's help= */}
      <p>
        The newest row in each currency is its box in Balances; the line above the table says whether it ties.
        If it says a currency does not tie, or that Balances says a different figure, two reads of the same
        money disagree &mdash; do not settle on those numbers until it is checked. Tap an entry to open it on
        Trades or Money movements, where its reference is.
      </p>
      {/* words:ok — About text, drawn only inside the party page's help= */}
      <p>
        Show reversed adds the reversed entries and the reversals that undid them. Over the whole history they
        net to nothing, so the balance ties either way; inside a date window they need not &mdash; an entry
        made inside the window and reversed after it leaves the window short by its amount.
      </p>
    </>
  );
}

export function PartyStatement({ slug, entity, balances, partyType, treasurySlug }: {
  slug: string;
  entity: string;
  /** The party page's own box — the numbers the Balances section renders. */
  balances: Record<string, number>;
  partyType: string;
  treasurySlug: string | null;
}) {
  // AN OWN ACCOUNT HAS NO BOX TO EXPLAIN. Its money sits on a desk account,
  // not in a party account, and the statement route reads only
  // customer:… / counterparty:… — so asking it about a desk account would
  // come back empty and print "nothing here", about an account that may hold
  // the desk's cash. The history it DOES have is the movement ledger,
  // filtered to that account; that is the door offered instead — the same
  // accessory link the party statement carries, and no sentence under it
  // (the explanation is in StatementHelp). A group has no account of its own
  // to open, so it has no Statement section at all.
  if (partyType === 'own_account') {
    if (!treasurySlug) return null;
    return (
      <Section
        title="Statement"
        icon={<ScrollText size={14} />}
        accessory={
          <Link to={`/movements?rail=${encodeURIComponent(treasurySlug)}`} className="tap-44 text-tg-link text-meta max-lg:p-2.5 max-lg:-m-2.5">
            Money movements →
          </Link>
        }
      >
        {null}
      </Section>
    );
  }
  // KEYED BY THE PARTY. Everything the operator picks below — the asset, the
  // period, whether reversals are shown — is state, and the party page does
  // not remount when you walk from one party to the next. Without this the
  // next party opens filtered to the last party's asset.
  return <PartyBoxStatement key={slug} slug={slug} entity={entity} balances={balances} />;
}

/* ══════════════════ THE ROW MODEL ══════════════════
 *
 * The route's row is a LEG: one (transaction, asset) pair with the signed
 * movement and the balance in that asset after it. The screen's row is the
 * same leg, in the ledger of its asset. A spot trade is therefore two rows in
 * two ledgers; a receipt is one row in one; the brought-forward row of a
 * windowed statement is one row at the bottom of each ledger that had a
 * position before the window. Nothing is re-summed here — `delta` and
 * `running` are the BFF's own numbers, carried leg by leg.
 */
interface Leg {
  /** null on the brought-forward row — no single transaction created it. */
  txn_id: string | null;
  at: string | null;
  kind: string;
  /** The BFF's sentence — the same on every leg of one transaction. */
  words: string;
  /** The deal id when the entry is a trade, else the ledger reference. */
  reference: string | null;
  /** The deal an entry belongs to — a trade or a cheque leg. Such an entry
   *  has no reverse door: its deal is the door. NEVER read for grouping —
   *  see buildJournal's own key, and linked_deal_id below for why. */
  deal_id: string | null;
  /** The deal a leg is LINKED to without being its own leg — a sub-broker's
   *  money-in/delivery-out, or a manager's-cheque leg (1 Oct 2026). Kept
   *  apart from `deal_id` on purpose: `deal_id` on an ordinary transfer
   *  would make the Reverse door refuse it (canReverse reads `deal_id`
   *  alone, below, and must go on doing so) while trade-void never reverses
   *  a transfer either, leaving it unreversible by any door. Absent on an
   *  older Worker. */
  linked_deal_id: string | null;
  state: MovementState;
  asset: string;
  delta: number;
  running: number;
}

/** One currency's ledger, NEWEST FIRST. `asset` is the folded code the chips
 *  use; each leg keeps the route's own asset code for the figures. */
interface Ledger { asset: string; legs: Leg[] }

/** Rows → one ledger per currency, in the order given (the chip order —
 *  biggest position first), holding only the currencies the rows on screen
 *  moved. Inside a ledger the route keeps the rows oldest→newest and the legs
 *  of one transaction adjacent, so two legs in the same asset inside one
 *  transaction (a reversal pair, say) add up into one row, the running
 *  balance being the later leg's. The display is then turned NEWEST FIRST
 *  (operator, 11 Sept 2026: "this order needs revert, newest first, on all
 *  screens"), so the top row's balance IS the box above and the
 *  brought-forward row, if any, sits at the bottom. */
function buildLedgers(rows: StatementRow[], order: string[]): Ledger[] {
  const assets = [...order];
  for (const r of rows) if (!assets.some((a) => sameAsset(a, r.asset))) assets.push(foldAsset(r.asset));
  const out: Ledger[] = [];
  for (const a of assets) {
    const legs: Leg[] = [];
    for (const r of rows) {
      if (!sameAsset(r.asset, a)) continue;
      const last = legs[legs.length - 1];
      if (last && last.txn_id === r.txn_id) { last.delta += r.delta; last.running = r.running; continue; }
      legs.push({
        txn_id: r.txn_id, at: r.at, kind: r.kind, words: r.words,
        reference: r.deal_id ?? r.reference, deal_id: r.deal_id,
        linked_deal_id: r.linked_deal_id ?? null, state: r.state,
        asset: r.asset, delta: r.delta, running: r.running,
      });
    }
    if (legs.length) out.push({ asset: a, legs: legs.reverse() });
  }
  return out;
}

/** The ledger types that are plain money for a party: the tag says which way
 *  it went, read off the sign, because "Money" on its own is the one word an
 *  operator has to stop and think about. */
const MONEY_KINDS = new Set(['transfer', 'party_payment_split', 'receipt', 'payment', 'movement']);

/** Is this leg a trade's — the Trades log's row rather than Money movements'? */
function isTrade(e: Leg): boolean {
  const k = e.kind.toLowerCase();
  return k === 'trade' || k === 'trade_unsettled';
}

/** The small tag at the head of the row: WHAT KIND of entry this is, in one
 *  or two words — the Money movements screen's words, so the same entry is
 *  tagged the same on both (§5.18): "Money in / Money out" by the sign,
 *  "Between parties", "Adjustment" for a correction. Every other kind reads
 *  off the one ledger-type table in lib/naming — the cheque FX pair included,
 *  which is a cheque leg and says so ("coin applied to the cheque" is the
 *  sentence); a word of its own here would be a second table. */
function entryTag(e: Leg): string {
  if (e.txn_id === null) return 'Brought forward';
  const k = e.kind.toLowerCase();
  if (isTrade(e)) return 'Trade';
  if (k === 'p2p') return 'Between parties';
  if (MONEY_KINDS.has(k)) {
    // Sign law: negative pushed the box DOWN — money came in from them.
    return e.delta === 0 ? 'Money' : e.delta < 0 ? 'Money in' : 'Money out';
  }
  return ledgerTypeLabel(k);
}

/** WHERE THE ENTRY LIVES — the screen that shows it with its reference and
 *  its panel. A trade is a Trades-log row (the search box takes a deal id);
 *  money is a Money-movements row (the search box takes a reference). */
function entryHref(e: Leg): string | null {
  if (!e.reference) return null;
  return isTrade(e)
    ? `/trades?q=${encodeURIComponent(e.reference)}`
    : `/movements?q=${encodeURIComponent(e.reference)}`;
}

/** The sentence, cut to what a column can hold. The BFF writes "bought X for
 *  Y — settle later" and "received X through Office cash"; the head is the
 *  particulars, the tail after the dash goes to the tooltip — unless the
 *  leg moved nothing, in which case the tail ("settled on the spot") is the
 *  only thing on the row that explains the blank, and it stays. Reversal
 *  words come off: the row already carries its state beside the tag. */
function particulars(words: string): { head: string; tail: string | null } {
  let w = words.replace(/^reversal — /, '').replace(/ \(later reversed\)$/, '');
  let tail: string | null = null;
  const i = w.indexOf(' — ');
  if (i > 0) { tail = w.slice(i + 3); w = w.slice(0, i); }
  return { head: w.charAt(0).toUpperCase() + w.slice(1), tail };
}

/** THE ENTRY AS THE DOOR SEES IT, built from THIS leg: the asset, the amount
 *  and the direction are the ledger's own. The statement knows the party and
 *  the leg, not the account the money went through — so "Book it again"
 *  opens Record money with the party, the amount, the asset and the time, and
 *  leaves the account for the operator to pick. Sign law: a negative movement
 *  pushed the box down — money came in from them. A trade's legs carry no
 *  door — canReverse keeps it off, the deal is the door. */
function reversible(e: Leg, party: { slug: string; display_name: string; entity?: string }): ReversibleEntry | null {
  if (e.txn_id === null) return null;
  return {
    txn_id: e.txn_id, reference: e.reference, kind: e.kind, words: e.words, at: e.at,
    state: e.state, deal_id: e.deal_id, party,
    asset: e.asset,
    amount: Math.abs(e.delta),
    direction: e.delta < 0 ? 'in' : 'out',
  };
}

/** Which Dubai day a leg sits under. The brought-forward row is not an
 *  event — it is everything before the window — so it gets a day of its own
 *  rather than borrowing the oldest row's. */
function dayKey(e: Leg): string {
  if (e.txn_id === null) return 'before';
  return e.at ? dubaiDate(e.at) : 'undated';
}
function dayLabel(e: Leg, windowed: boolean): string {
  if (e.txn_id === null) return windowed ? 'Before this period' : 'Earlier';
  if (!e.at) return 'Undated';
  const b = dateBucket(e.at);
  return b === 'today' ? 'Today' : b === 'yesterday' ? 'Yesterday' : fmtWeekday(e.at);
}

/** What one ledger's header may say about its top figure. */
interface Tie { asset: string; end: number; held: number; shown: number; ok: boolean; agrees: boolean }

function PartyBoxStatement({ slug, entity, balances }: {
  slug: string;
  entity: string;
  balances: Record<string, number>;
}) {
  // EVERY CURRENCY, unless the operator narrows it. It used to open filtered
  // to the party's biggest currency, so "how the balance arrived" showed one
  // ledger of a several-ledger story and the other assets looked like they
  // had no history at all (operator, 4 Sept: "arranging all the balance into
  // a single table, starting with opening").
  const [asset, setAsset]   = useState<string>('');
  const [undone, setUndone] = useState(false);
  // The whole history by default: a statement's top row is the box above,
  // and a window that stops early stops claiming that.
  const [period, setPeriod] = useState<PeriodValue>({ key: 'all' });
  const win = periodDays(period);

  const q = useInfiniteQuery({
    queryKey: ['statement', slug, entity, asset, win.from ?? '', win.to ?? '', undone],
    queryFn: ({ pageParam }) => api.partyStatement(slug, {
      entity,
      asset: asset || undefined,
      from: win.from,
      to:   win.to,
      limit: PAGE,
      cursor: pageParam || undefined,
      include_undone: undone,
    }),
    initialPageParam: '',
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });

  // Every page after the first carries its OWN "balance brought forward" row —
  // correct on its own, noise in the middle of a list that already showed the
  // rows it sums up. The first page's opening row stays: without it the
  // arithmetic on screen is honest and the answer is still wrong.
  const rows: StatementRow[] = useMemo(() => {
    const pages = q.data?.pages ?? [];
    return pages.flatMap((p, i) => (i === 0 ? p.rows : p.rows.filter((r) => r.txn_id !== null)));
  }, [q.data]);

  const box = q.data?.pages[0]?.box ?? [];
  const ever = q.data?.pages[0]?.assets ?? [];
  // Who this statement is about, as the route names them — for the door's
  // read-back and the "Book it again" link. Until the first read lands the
  // slug stands in (partyName cleans a bare slug up the way every screen does).
  const party = q.data?.pages[0]?.party ?? { slug, display_name: slug, entity };
  // The last entry reversed from a row (see the header).
  const [reversed, setReversed] = useState<ReversedOutcome | null>(null);

  // EVERY ASSET THIS PARTY EVER TOUCHED — the chip list, and not today's box.
  //
  // The party page's box drops every asset that has netted to ZERO. A
  // counterparty who settled his dollars and still owes dirhams therefore got
  // one chip, no way to reach the dollars, and — because one option renders no
  // switch at all — no sign that a second asset existed. On this desk that is
  // not an edge case: most parties are square in at least one asset, which is
  // exactly what a settled trade leaves behind. The statement's own `box` and
  // `assets` are read across every asset the party has moved; the party page's
  // balances are folded in as well so the chips are complete before the first
  // read lands and nothing appears late.
  const assetOptions = useMemo(() => {
    const weight = new Map<string, number>();
    const note = (code: string, v: number) => {
      const a = foldAsset(code);
      weight.set(a, Math.max(weight.get(a) ?? 0, Math.abs(v)));
    };
    for (const [code, v] of Object.entries(balances ?? {})) note(code, v);
    for (const b of box) note(b.asset, b.balance);
    for (const a of ever) if (!weight.has(foldAsset(a.asset))) weight.set(foldAsset(a.asset), 0);
    return [...weight.entries()]
      .sort((x, y) => y[1] - x[1] || x[0].localeCompare(y[0]))
      .map(([a]) => a);
  }, [box, balances, ever]);

  // THE LEDGERS. One per currency the rows on screen actually moved, in the
  // chip order (biggest position first) — an asset the party touched once in
  // June is a chip, not an empty ledger on this month's statement. With a
  // chip chosen the route already filters the rows, so one ledger stands.
  const ledgers = useMemo(
    () => buildLedgers(rows, asset ? assetOptions.filter((a) => sameAsset(a, asset)) : assetOptions),
    [rows, asset, assetOptions],
  );

  // WHICH FAILURE THIS IS DECIDES WHAT MAY STAY ON SCREEN.
  //
  // In TanStack v5 a failed "Load more" — or a failed refetch — puts the
  // WHOLE query into the error state, and this section answered that by
  // stacking "nothing was read" on top of a table that was visibly showing
  // history. `q.data` is the honest test: it is undefined only while no page
  // has ever landed, which is the only case in which nothing was in fact read.
  const nothingRead  = q.isError && !q.data;
  const pagingError  = q.isFetchNextPageError ? q.error : null;
  const refreshError = q.isError && !!q.data && !q.isFetchNextPageError ? q.error : null;

  // The last balance this page reached, per asset — each ledger has to tie
  // on its own.
  const ends = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of rows) m.set(r.asset, r.running);
    return m;
  }, [rows]);

  const ties = useMemo<Tie[]>(
    () => [...ends.entries()].map(([a, end]) => {
      // What the ledger says this party's position IS, read the same exact way
      // the running balance was summed…
      const held = box.find((b) => sameAsset(b.asset, a))?.balance ?? 0;
      // …and what the Balances section directly above is rendering. Claiming a
      // tie to a number the operator cannot see would be the one lie this
      // section must not tell.
      const shownBalance = Object.entries(balances ?? {})
        .find(([code]) => sameAsset(code, a))?.[1] ?? 0;
      return {
        asset: a, end, held, shown: shownBalance,
        ok: Math.abs(end - held) < AGREE_EPSILON,
        agrees: Math.abs(held - shownBalance) < AGREE_EPSILON,
      };
    }),
    [ends, box, balances],
  );

  // A WINDOW THAT STOPS IN THE PAST DOES NOT REACH TODAY'S BOX. The top row
  // is then the balance on the closing day, and calling that a tie would be
  // false for every party who has moved money since. An open top — no closing
  // day, or one at or after today — still ties, because the brought-forward
  // row carries everything before the opening one.
  const tiesToToday = !win.to || win.to >= dubaiDate();
  const windowed = !!win.from;

  return (
    <Section
      title="Statement"
      icon={<ScrollText size={14} />}
      accessory={
        <Link to={`/movements?party=${encodeURIComponent(slug)}`} className="tap-44 text-tg-link text-meta max-lg:p-2.5 max-lg:-m-2.5">
          Money movements →
        </Link>
      }
    >
      {/* The chips, no labels: an asset code is an asset, "This month" is a
          period, and the two rows read faster than two captions and three
          rows did. The words are the Money movements screen's (§6). */}
      <div className="space-y-1.5 pb-2">
        {assetOptions.length > 1 && (
          <div className="flex flex-wrap items-center gap-1.5 lg:gap-1">
            <Chip active={asset === ''} onClick={() => setAsset('')}>All</Chip>
            {assetOptions.map((a) => (
              <Chip key={a} active={asset === a} onClick={() => setAsset(a)}>{displayAsset(a)}</Chip>
            ))}
          </div>
        )}
        {/* SWEEP, 21 Oct 2026 — the chips wrap on a phone. PeriodChips is a sideways strip there, and this statement opens on
            its LAST chip, "All time": lit, and 183 px past the edge of a 390 px screen, so the period in force could not be
            seen. Here the strip dissolves (display: contents, as it already does from lg) and the chips wrap with "Show
            reversed" — the chips are PeriodChips' own. */}
        <div className="flex flex-wrap items-start gap-1.5 lg:gap-1">
          <PeriodChips value={period} onChange={setPeriod} allTime className="contents [&>div:first-child]:contents" />
          <Chip active={undone} onClick={() => setUndone((v) => !v)}>Show reversed</Chip>
        </div>
      </div>

      <ReversedLine outcome={reversed} onDismiss={() => setReversed(null)} className="mb-2" />

      {q.isPending && <Pending what="Reading…" />}

      {/* A FAILED FIRST READ IS NOT A CLEAN STATEMENT. No ledger, no zeroes, no
          tie word — the API's own sentence, which already says nothing was
          read; a section that quietly shows nothing would say the party has
          no history. */}
      {/* Sweep, 21 Oct 2026: ONE line (what failed; Try again), the API's sentence a tap away — red, because nothing was read. */}
      {nothingRead && (
        <StateLine
          tone="bad"
          more={readWords(q.error)}
          action={<button type="button" className="btn-link text-body" disabled={q.isFetching} onClick={() => q.refetch()}>{q.isFetching ? 'Trying…' : 'Try again'}</button>}
        >
          Couldn&rsquo;t read the statement.
        </StateLine>
      )}

      {/* A FAILED REFRESH IS A FAILED READ (Astra, 22 Oct): ONE red line with its Try again — it says what failed AND what is on
          screen. The rows below, when there are some, were read and are kept — they are simply not the newest. With none
          cached the old read was an empty statement, and an empty statement that could not be refreshed is not "Nothing here":
          the line stands alone, like a first read that failed. */}
      {refreshError && (
        <StateLine
          tone="bad"
          more={readWords(refreshError)}
          action={<button type="button" className="btn-link text-body" disabled={q.isFetching} onClick={() => q.refetch()}>{q.isFetching ? 'Trying…' : 'Try again'}</button>}
        >
          {rows.length > 0 ? <>Couldn&rsquo;t refresh — showing the last good read.</> : <>Couldn&rsquo;t refresh the statement.</>}
        </StateLine>
      )}

      {q.isSuccess && rows.length === 0 && (
        <EmptyState className="!py-3">
          Nothing here{asset ? ` in ${displayAsset(asset)}` : ''}{windowed || win.to ? ' this period' : ''}.
        </EmptyState>
      )}

      {ledgers.length > 0 && (
        <>
          <Journal
            ledgers={ledgers}
            ties={ties}
            // UNTIL THE LAST PAGE IS IN, the top row is mid-history and must
            // never be read as the party's position — so no tie word until it is.
            unread={!!q.hasNextPage}
            closesOn={!tiesToToday && win.to ? fmtDay(win.to) : null}
            windowed={windowed}
            party={party}
            onReversed={setReversed}
          />

          {q.hasNextPage && (
            <div className="pt-2 text-center">
              <button
                type="button"
                className="btn-secondary text-body py-1.5 px-3 inline-flex items-center justify-center max-lg:min-h-11"
                disabled={q.isFetchingNextPage}
                onClick={() => q.fetchNextPage()}
              >
                {q.isFetchingNextPage ? 'Reading…' : 'Load more'}
              </button>
              {/* The ledger headers withhold their tie word while a page is
                  unread; this is the one line that says why. */}
              <WarnLine className="mt-1 text-body">More to read</WarnLine>
            </div>
          )}

          {pagingError && (
            <StateLine tone="warn" className="mt-2" more={readWords(pagingError)}>The rest of the history has not been read.</StateLine>
          )}
        </>
      )}
    </Section>
  );
}

/** The sentence for one leg: the head of the BFF's words — the other side of
 *  a trade is already in them ("Bought 53,947 EUR for 2,26,115 AED"), so a
 *  per-currency ledger needs no suffix. It is the door to the entry: a trade
 *  opens on the Trades log, money on Money movements, each found by its
 *  reference. A row that is not live says so in one word ahead of the
 *  sentence — the reversal in the warning colour — because the pill in the
 *  Type column names the kind, not the state. */
function Words({ e }: { e: Leg }) {
  if (e.txn_id === null) return null;
  const p = particulars(e.words);
  const href = entryHref(e);
  const body = (
    <>
      {p.head}
      {e.delta === 0 && p.tail && <span className="text-tg-hint"> · {p.tail}</span>}
    </>
  );
  return (
    <>
      {e.state !== 'live' && (
        <span className={e.state === 'reversal' ? 'text-warn' : 'text-tg-hint'}>
          {e.state === 'reversal' ? 'Reversal' : 'Reversed'} ·{' '}
        </span>
      )}
      {href ? <Link to={href} className="hover:underline">{body}</Link> : body}
    </>
  );
}

/* ══════════════════ THE JOURNAL ══════════════════ */

/** One row of the journal: a transaction, with a leg for each currency it
 *  moved, in the chips' order (biggest position first). `lead` is its
 *  first leg — the words, the kind, the state and the reference are the
 *  same on every leg of one transaction. The brought-forward balances of a
 *  windowed statement are one row (key 'before') with a leg per currency
 *  and no movement. */
interface JournalRow {
  key: string;
  txn_id: string | null;
  at: string | null;
  lead: Leg;
  legs: Leg[];
}

/** The ledgers' legs regrouped by transaction, newest first. buildLedgers
 *  has already folded two legs of one transaction in one currency into one
 *  leg, so a leg here is one figure in one currency. */
function buildJournal(ledgers: Ledger[]): JournalRow[] {
  // `linked_deal_id` ALONE decides whether a leg joins another
  // transaction's row — never bare `deal_id` (1 Oct 2026, the same rule and
  // the same reason as MovementTable.tsx's journalEntries: `deal_id` also
  // marks one side of a manager's cheque, and manager-cheque.ts stamps the
  // SAME deal_id on the customer's deposit and the seller's payment — two
  // different parties, one deal. A party's own statement can never show
  // both sides of one MC (each leg touches only one of the two parties), so
  // that particular collision cannot happen here — but the rule is kept
  // the same as Home's anyway: `linked_deal_id` means exactly one thing,
  // one party's money legs of one deal, and needs no per-file exception).
  // `deal_id` itself is NEVER touched by this grouping and stays exactly
  // what buildLedgers set it to — reversible() (below) reads it raw, so a
  // linked leg stays reversible through the ordinary door, same as it
  // already is on Money movements.
  const rows = new Map<string, JournalRow>();
  for (const L of ledgers) {
    for (const e of L.legs) {
      const key = e.linked_deal_id ?? e.txn_id ?? 'before';
      const row = rows.get(key);
      if (!row) {
        rows.set(key, { key, txn_id: e.txn_id, at: e.at, lead: e, legs: [e] });
        continue;
      }
      row.legs.push(e);
      // A linked_deal_id group can span more than one transaction, and this
      // loop visits legs per-currency (one ledger at a time) rather than
      // globally newest-first the way a single transaction's own legs
      // already are — so which leg leads has to be decided by comparing
      // timestamps explicitly, never by "first seen wins".
      if ((e.at ?? '') > (row.at ?? '')) { row.at = e.at; row.txn_id = e.txn_id; row.lead = e; }
    }
  }
  // A group spanning more than one transaction reads as a Trade — the pill
  // (entryTag) and the sentence (Words) both read `lead`, so the two can
  // never disagree about what kind of row this is; `reference` moves to the
  // deal too, so entryHref opens the deal panel rather than one leg's own
  // payment reference, which a Trades-log search would not find.
  for (const row of rows.values()) {
    const distinctTxns = new Set(row.legs.map((l) => l.txn_id)).size;
    if (distinctTxns > 1 && row.lead.kind.toLowerCase() !== 'trade') {
      row.lead = { ...row.lead, kind: 'trade', reference: row.lead.linked_deal_id ?? row.lead.reference };
    }
  }
  return [...rows.values()].sort((a, b) => {
    if (a.txn_id === null) return 1;
    if (b.txn_id === null) return -1;
    const ta = a.at ?? '', tb = b.at ?? '';
    if (ta !== tb) return ta < tb ? 1 : -1;
    return Number(b.txn_id) - Number(a.txn_id);
  });
}

/** TWO SEATS, MOVEMENT AND BALANCE — the bank statement's (operator, 22
 *  Sept 2026, on a column per currency: "the prevailing totals and trade +
 *  & − can be displayed in a better way"). A column per currency left most
 *  of the grid blank on a five-currency party and stacked two mono figures
 *  in the cells that had anything. Now every entry has one Movement seat
 *  and one Balance seat, the currency code muted on the figure; a trade's
 *  two legs are two lines inside its one row. Any number of currencies, no
 *  empty column, the same 60rem the ledger tables have. Balances of one
 *  currency read down one line once its chip is chosen. The seat itself is
 *  LEDGER.figWide — Home's journal stands on the same one (24 Sept 2026), and
 *  a second copy of the width is how two journals start drifting apart. */
const FIG = LEDGER.figWide;

/** What a column's header says under its code (the old ledger header's
 *  one word): nothing while a page is unread; the closing day when the
 *  window stops before today, with no tie claimed; "ties" when the top row
 *  is the box above; otherwise the two figures that disagree, in the
 *  warning colour, at the precision they differ at — what to do about it
 *  is in StatementHelp. */
function tieNote(tie: Tie | null, unread: boolean, closesOn: string | null): ReactNode {
  if (unread) return null;
  if (closesOn) return <>balance on {closesOn}</>;
  if (!tie) return null;
  if (tie.ok && tie.agrees) return <>ties</>;
  const against = !tie.ok ? tie.held : tie.shown;
  const dp = reconcileDp(tie.end, against);
  return (
    <span className="text-warn">
      {tie.ok ? 'Balances above says' : 'does not tie — books hold'}{' '}
      <span className="amt">{fmtAmount(against, tie.asset, dp)}</span>
      {' · '}here <span className="amt">{fmtAmount(tie.end, tie.asset, dp)}</span>
    </span>
  );
}

/** THE TIE WORDS, one line above the journal at every width: the code and
 *  its word for each currency — a warn note with its two figures is wider
 *  than any column header, so none of them sits in one. */
function TieLine({ assets, tieOf, unread, closesOn }: {
  assets: string[];
  tieOf: (a: string) => Tie | null;
  unread: boolean;
  closesOn: string | null;
}) {
  const notes = assets.map((a) => [a, tieNote(tieOf(a), unread, closesOn)] as const).filter(([, n]) => n);
  if (notes.length === 0) return null;
  return (
    <div className="px-2 pb-1.5 text-meta text-tg-hint flex flex-wrap gap-x-4 gap-y-0.5">
      {notes.map(([a, n]) => <span key={a}><span className="text-tg-text">{displayAsset(a)}</span> {n}</span>)}
    </div>
  );
}

/** THE JOURNAL: the table at lg+, the cards below it — the same rows, the
 *  same day rows, the same door. Its shape is the console's one ledger
 *  primitive (components/Ledger): the widths, the sizes and the colours come
 *  from there, capped at LEDGER.block so the figures sit near the words. */
function Journal({ ledgers, ties, unread, closesOn, windowed, party, onReversed }: {
  ledgers: Ledger[];
  ties: Tie[];
  /** A page is still unread — the top row is mid-history. */
  unread: boolean;
  /** The window's closing day, when it stops before today; null when the
   *  top row may claim today's box. */
  closesOn: string | null;
  windowed: boolean;
  party: { slug: string; display_name: string; entity?: string };
  onReversed: (o: ReversedOutcome) => void;
}) {
  const assets = ledgers.map((L) => L.asset);
  const rows = useMemo(() => buildJournal(ledgers), [ledgers]);
  if (rows.length === 0) return null;
  const tieOf = (a: string) => ties.find((t) => sameAsset(t.asset, a)) ?? null;
  const cols = 7;   // when · type · words · reverse · movement · balance · slack

  return (
    <div className={LEDGER.block}>
      <TieLine assets={assets} tieOf={tieOf} unread={unread} closesOn={closesOn} />

      {/* lg+: the table — the ledger's own seats, with Movement and Balance
          carrying every currency. */}
      <div className="hidden lg:block overflow-x-auto">
        <table className={`${LEDGER.table} min-w-[880px]`}>
          <thead>
            <tr>
              <th className={`${LEDGER.th} ${LEDGER.whenW}`}>When</th>
              <th className={`${LEDGER.th} ${LEDGER.kind}`}>Type</th>
              <th className={`${LEDGER.th} ${LEDGER.words}`}>Particulars</th>
              <th className={`${LEDGER.th} ${LEDGER.act}`} aria-label="Reverse" />
              <th className={`${LEDGER.th} ${FIG}`}>Movement</th>
              <th className={`${LEDGER.th} ${FIG}`}>Balance</th>
              <th className={LEDGER.slack} aria-hidden="true" />
            </tr>
          </thead>
          <tbody>
            {/* KEYED BY THE ENTRY, not its position, so a refresh that lands
                a new entry above does not remount an open reverse panel. */}
            {rows.map((r, i) => {
              const prev = rows[i - 1];
              const newDay = !prev || dayKey(prev.lead) !== dayKey(r.lead);
              return (
                <Fragment key={r.key}>
                  {newDay && <DayRow label={dayLabel(r.lead, windowed)} colSpan={cols} />}
                  <JournalLine row={r} cols={cols} party={party} onReversed={onReversed} />
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Below lg: a card per entry. */}
      <div className="lg:hidden">
        {rows.map((r, i) => {
          const prev = rows[i - 1];
          const newDay = !prev || dayKey(prev.lead) !== dayKey(r.lead);
          return (
            <Fragment key={r.key}>
              {newDay && <div className={LEDGER.day}>{dayLabel(r.lead, windowed)}</div>}
              <JournalCard row={r} single={assets.length === 1} party={party} onReversed={onReversed} />
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}

/** ONE DOOR PER TRANSACTION INSIDE THE ROW (1 Oct 2026 — Bookkeeping, on the
 *  group merging landed the same day: buildJournal flips a merged row's
 *  `lead.kind` to 'trade' so the pill and the sentence agree, but
 *  canReverse has its OWN `kind !== 'trade'` gate, separate from deal_id —
 *  reading the flipped lead there would have silently taken the door away
 *  from two ordinary, independently reversible transfers, exactly the trap
 *  `deal_id` vs `linked_deal_id` already exists to avoid one field over).
 *
 *  So doors are read from `row.legs`, which buildJournal never rewrites —
 *  each leg keeps its own real kind, deal_id and state. Keyed by txn_id,
 *  first occurrence anchors it: an ORDINARY row (one transaction, one or
 *  several currencies — a between-parties swap, an ordinary trade) still
 *  gets exactly the ONE door it always had, because canReverse and
 *  deal_id decide reversibility exactly as before, per leg, and a
 *  same-txn_id currency pair collapses to one match here just as it always
 *  did. Only a row a deal_id/linked_deal_id merge actually combined —
 *  distinct txn_ids sharing one row — can ever produce more than one. */
interface LegDoor { leg: Leg; entry: ReversibleEntry; }
function legDoors(row: JournalRow, party: { slug: string; display_name: string; entity?: string }): LegDoor[] {
  const seen = new Set<string>();
  const doors: LegDoor[] = [];
  for (const leg of row.legs) {
    const k = leg.txn_id ?? '';
    if (!k || seen.has(k)) continue;
    seen.add(k);
    const entry = reversible(leg, party);
    if (entry && canReverse(entry)) doors.push({ leg, entry });
  }
  return doors;
}

/** The tooltip: the whole sentence, and the reference the row no longer
 *  prints. */
function rowTitle(e: Leg): string {
  return e.reference ? `${e.words}\n${e.reference}` : e.words;
}

/** One entry on the lg+ table: the time, the type pill, the sentence, the
 *  reverse door (or its empty seat), then the Movement seat and the Balance
 *  seat — one line per currency the entry moved, the code muted on the
 *  figure, a trade's two legs two lines. The brought-forward row has no
 *  time and no movements — balances only. */
function JournalLine({ row, cols, party, onReversed }: {
  row: JournalRow;
  cols: number;
  party: { slug: string; display_name: string; entity?: string };
  onReversed: (o: ReversedOutcome) => void;
}) {
  const e = row.lead;
  const before = row.txn_id === null;
  const dim = e.state === 'live' ? '' : LEDGER.rowDim;
  const doors = legDoors(row, party);
  const doorByTxn = new Map(doors.map((d) => [d.leg.txn_id, d]));
  // The ordinary case — never more than one door for this row — keeps the
  // exact seat it always had: one button in the Act column, not aligned to
  // any particular currency line, same as before this row could ever be a
  // merge. Only when a merge genuinely produced more than one does the Act
  // column switch to a line per leg, matching Movement and Balance's own
  // shape, so each door sits beside the figure it belongs to.
  const oneDoorForRow = doors.length === 1 ? doors[0] : undefined;
  const [openTxn, setOpenTxn] = useState<string | null>(null);
  const openDoor = openTxn ? doorByTxn.get(openTxn) : undefined;
  return (
    <>
    <tr className={`border-b border-tg-secondary ${dim} ${openDoor ? 'border-b-0' : ''}`} title={rowTitle(e)}>
      <td className={`${LEDGER.td} ${LEDGER.when}`}>
        {before || !e.at ? '' : fmtTime(e.at)}
      </td>
      <td className={`${LEDGER.td} ${LEDGER.kind}`}>
        <KindPill label={entryTag(e)} />
      </td>
      <td className={`${LEDGER.td} ${LEDGER.words}`}>
        {/* 22 Oct 2026: two lines, not one cut mid-word — a 9-digit trade's sentence ("Bought 2,000,000.00 USDT for 7,340,000 AED @ …")
            ended in an ellipsis before its rate; the row grows only when the sentence does, the whole of it stays on the row's tooltip. */}
        {!before && <div className="line-clamp-2 break-words leading-snug"><Words e={e} /></div>}
      </td>
      <td className={`${LEDGER.td} ${LEDGER.act}`}>
        {oneDoorForRow ? (
          <ReverseEntryButton
            short open={openTxn === oneDoorForRow.leg.txn_id}
            onClick={() => setOpenTxn((v) => (v === oneDoorForRow.leg.txn_id ? null : oneDoorForRow.leg.txn_id))}
          />
        ) : doors.length > 1 && row.legs.map((l) => {
          const d = l.txn_id ? doorByTxn.get(l.txn_id) : undefined;
          return (
            <div key={l.asset} className="leading-snug">
              {d && d.leg === l && (
                <ReverseEntryButton short open={openTxn === l.txn_id} onClick={() => setOpenTxn((v) => (v === l.txn_id ? null : l.txn_id))} />
              )}
            </div>
          );
        })}
      </td>
      <td className={`${LEDGER.td} ${FIG}`}>
        {!before && row.legs.map((l) => (
          <div key={l.asset} className="leading-snug"><Delta value={l.delta} asset={l.asset} code /></div>
        ))}
      </td>
      <td className={`${LEDGER.td} ${FIG}`}>
        {row.legs.map((l) => (
          <div key={l.asset} className="leading-snug">
            <Figure value={l.running} asset={l.asset} /> <span className="text-tg-hint">{displayAsset(l.asset)}</span>
          </div>
        ))}
      </td>
      <td className={LEDGER.slack} aria-hidden="true" />
    </tr>
    {/* The panel, beneath the row, across every column — for whichever
        door is open, when more than one exists. KEYED BY txn_id (CEX
        session's review, same evening): without a key, switching which
        door is open swaps only the `entry` prop, and React reuses the
        panel instance — its own reason/error/drawn state (ReverseEntry.tsx)
        would carry over from the transaction just closed to the one just
        opened. On a control that reverses money, a stale "already warned"
        or "already drawn" state read against the WRONG transaction is not
        a cosmetic bug. */}
    {openDoor && (
      <tr className="border-b border-tg-secondary">
        <td colSpan={cols} className="px-2 pb-2 pt-0">
          <ReverseEntryPanel key={openDoor.entry.txn_id} entry={openDoor.entry} onClose={() => setOpenTxn(null)} onReversed={onReversed} />
        </td>
      </tr>
    )}
    </>
  );
}

/** One entry below lg: the type pill and the sentence (two lines of it at
 *  most); then the time and the reverse door — and, on a party in ONE
 *  currency, the movement and the balance after it on that same line. On a
 *  party in several currencies every entry lists a line per currency it
 *  moved beneath: the code, the movement, the balance — the same shape for
 *  a receipt and a trade, so the balances read down one line; a code
 *  squeezed onto the figures' line beside a Reverse door did not fit 375px. */
function JournalCard({ row, single: oneCurrency, party, onReversed }: {
  row: JournalRow;
  /** The party moves one currency: the figures sit on the second line. */
  single: boolean;
  party: { slug: string; display_name: string; entity?: string };
  onReversed: (o: ReversedOutcome) => void;
}) {
  const e = row.lead;
  const before = row.txn_id === null;
  const dim = e.state === 'live' ? '' : LEDGER.rowDim;
  const doors = legDoors(row, party);
  const doorByTxn = new Map(doors.map((d) => [d.leg.txn_id, d]));
  // The ordinary case keeps its one door in cardLine2's centre seat, exactly
  // where it always sat, whether the row moved one currency or several —
  // only a genuine merge (more than one door) moves the doors down into the
  // per-leg block below, one beside each figure it belongs to.
  const oneDoorForRow = doors.length === 1 ? doors[0] : undefined;
  const [openTxn, setOpenTxn] = useState<string | null>(null);
  const openDoor = openTxn ? doorByTxn.get(openTxn) : undefined;
  const single = oneCurrency && row.legs.length === 1 ? row.legs[0]! : null;
  // THE WHOLE ROW OPENS ITS ENTRY on the rebuilt console (the Command Centre's ruling, 27 Oct 2026): the sentence stays
  // clamped, and a clamp clips any invisible tap box round its link, so the card is the tap — as Onboarding's cards are.
  // A tap on a control (the Reverse door, a link), a selection, or a row whose Reverse panel is open opens nothing.
  // Today's console keeps its link alone.
  const navigate = useNavigate();
  const href = !before && uiV2(2) ? entryHref(e) : null;
  const openRow = href ? (ev: { target: EventTarget | null }) => {
    if (openTxn || (ev.target as Element | null)?.closest?.('a, button, input, textarea, select, label, summary, [role="button"], [data-no-open]')) return;
    if ((window.getSelection?.()?.toString() ?? '').length > 0) return;
    navigate(href);
  } : undefined;
  return (
    <div className={`${LEDGER.card} px-2 ${dim} ${href ? 'cursor-pointer' : ''}`} title={rowTitle(e)}
      onClick={openRow} data-opens={href ? '' : undefined}>
      <div className={LEDGER.cardLine1}>
        <KindPill label={entryTag(e)} className="shrink-0 mt-px" />
        {/* 22 Oct 2026: three lines, not two — on a 9-digit trade the clamp cut "· settled on the spot", the one phrase that explains the
            blank movement beneath it (the particulars' own comment). */}
        {!before && <span className="min-w-0 line-clamp-3 break-words"><Words e={e} /></span>}
      </div>
      <div className={LEDGER.cardLine2}>
        <span className={`${LEDGER.when} shrink-0`}>
          {before || !e.at ? '' : fmtTime(e.at)}
        </span>
        {single && !before && <span className="whitespace-nowrap shrink-0"><Delta value={single.delta} asset={single.asset} /></span>}
        <span className="flex-1 min-w-0 text-center">
          {oneDoorForRow && (
            <ReverseEntryButton
              short plain open={openTxn === oneDoorForRow.leg.txn_id}
              onClick={() => setOpenTxn((v) => (v === oneDoorForRow.leg.txn_id ? null : oneDoorForRow.leg.txn_id))}
            />
          )}
        </span>
        {single && (
          <span className="whitespace-nowrap shrink-0"><Figure value={single.running} asset={single.asset} /></span>
        )}
      </div>
      {!single && (
        <div className="mt-0.5 space-y-0.5 text-meta">
          {row.legs.map((l) => {
            const anchor = doors.length > 1 && l.txn_id ? doorByTxn.get(l.txn_id) : undefined;
            const isAnchor = !!anchor && anchor.leg === l;
            return (
              <div key={l.asset} className="flex items-baseline gap-2">
                <span className="w-12 shrink-0 text-tg-hint text-meta">{displayAsset(l.asset)}</span>
                <span className="flex-1 whitespace-nowrap">{!before && <Delta value={l.delta} asset={l.asset} />}</span>
                {isAnchor && (
                  <ReverseEntryButton short plain open={openTxn === l.txn_id} onClick={() => setOpenTxn((v) => (v === l.txn_id ? null : l.txn_id))} />
                )}
                <span className="whitespace-nowrap"><Figure value={l.running} asset={l.asset} /></span>
              </div>
            );
          })}
        </div>
      )}
      {/* Keyed the same way and for the same reason as JournalLine's panel
          above — see its own comment. */}
      {openDoor && (
        <ReverseEntryPanel key={openDoor.entry.txn_id} entry={openDoor.entry} onClose={() => setOpenTxn(null)} onReversed={onReversed} className="mt-2" />
      )}
    </div>
  );
}
